import { queryOptions } from "@tanstack/react-query"
import { api } from "./api"
import { listAll, listAllData } from "./list-all"
import type { DietaryAllergy, DietaryReport } from "./people"
import type { DietEntry } from "./dietary"

// Schedule + Session-record data layer. Query keys for the LIST resources match
// apps/admin byte-for-byte (`["resource", <table>, {eventId}]`) so the vendored
// realtime layer surgically patches the grid/timeline caches on live CRUD.
// The Session record bundle (`activity-detail`) is keyed by agendaItemId; its
// lifecycle mutations invalidate both it and the agenda-items resource cache.

// Who can read a session's "who's going" roster. Event column = default;
// agenda item column (nullable) = per-session override.
export type RosterVisibility = "admin-only" | "attendees-of-item" | "all-attendees"
export const ROSTER_VISIBILITY_OPTIONS: { value: RosterVisibility; label: string }[] = [
  { value: "admin-only", label: "Event team only" },
  { value: "attendees-of-item", label: "Attendees going to the session" },
  { value: "all-attendees", label: "All event attendees" },
]

// ── list-resource row types (mirror apps/admin SchedulePage) ────────────────
export type AgendaItem = {
  id: string
  eventId: string
  groupId: string | null
  activityId: string | null
  locationId: string | null
  position: number
  name: string
  description: string | null
  startsAt: string
  endsAt: string
  capacity: number | null
  accessMode: string
  visibility: "public" | "private" | "cohort" | "organizer" | string
  audienceGroupId: string | null
  preApproved: number
  isRequired: number
  /** 1 = highlighted card on attendees' schedules. Optional until every
   *  backend has the column. */
  isFeatured?: number
  /** 0 = hide attendees' Directions button. Absent ⇒ 1. */
  showDirections?: number
  /** 1 = attendees' schedule shows "Until <end>". Absent ⇒ 0. */
  showEndTime?: number
  /** 0 = keep off attendees' Home (On now / Up next). Absent ⇒ 1. */
  showOnHome?: number
  /** Per-session roster override; null ⇒ the event default. */
  sessionRosterVisibility?: RosterVisibility | null
  rsvpMode: string
  code: string | null
  status: string
  /** How a freed seat is filled: "queue" = next in line is auto-promoted,
   *  "claim" = every waitlisted guest is pushed and the first to tap takes it.
   *  Only meaningful when waitlistEnabled=1. */
  waitlistMode?: "queue" | "claim"
}

export type AgendaGroup = {
  id: string
  eventId?: string
  name: string
  description?: string | null
  selectionRule: "one" | "multi" | string
  position: number
  /** 1 = the group offers an explicit "None of these" opt-out. */
  allowNone?: number
  /** Group-level slot time, naive-local "YYYY-MM-DDTHH:mm" (may be null). Used
   *  to disambiguate same-named groups in pickers. */
  startsAt?: string | null
  endsAt?: string | null
}

/** Compact picker label for a group: "Saturday Morning · Sat Sep 12, 8:00am".
 *  Falls back to just the name when the group has no slot time. Because group
 *  names aren't unique, the day+time suffix is what makes options tell-apart-able
 *  in a flat `<select>`. */
export function groupPickerLabel(g: AgendaGroup): string {
  const day = (g.startsAt ?? "").slice(0, 10)
  if (!day) return g.name
  const when = `${formatWeekday(day)} ${formatDayMonth(day)}, ${formatTime(g.startsAt!)}`
  return `${g.name} · ${when}`
}

export type AgendaActivity = {
  id: string
  eventId?: string
  name: string
  description?: string | null
  minPerAttendee: number | null
  maxPerAttendee: number | null
  position?: number
}

export type LocationRow = {
  id: string
  parentLocationId: string | null
  name: string
  position: number
}


// ── query keys (must match apps/admin) ──────────────────────────────────────
export const agendaItemsKey = (eventId: string) =>
  ["resource", "agenda-items", { eventId }] as const
export const agendaGroupsKey = (eventId: string) =>
  ["resource", "agenda-groups", { eventId, mode: "schedule" }] as const
export const agendaActivitiesKey = (eventId: string) =>
  ["resource", "agenda-activities", { eventId, mode: "schedule" }] as const
export const locationsKey = (eventId: string) =>
  ["resource", "locations", { eventId, mode: "schedule" }] as const

// ── list query options ──────────────────────────────────────────────────────
export function agendaItemsQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: agendaItemsKey(eventId),
    queryFn: () =>
      listAllData<AgendaItem>("/api/v1/agenda-items", { eventId, sort: "startsAt", order: "asc" }),
    select: (r) => r.data,
  })
}

export function agendaGroupsQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: agendaGroupsKey(eventId),
    queryFn: () =>
      listAllData<AgendaGroup>("/api/v1/agenda-groups", { eventId }),
    select: (r) => r.data,
  })
}

export function agendaActivitiesQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: agendaActivitiesKey(eventId),
    queryFn: () =>
      listAllData<AgendaActivity>("/api/v1/agenda-activities", { eventId }),
    select: (r) => r.data,
  })
}

export function locationsQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: locationsKey(eventId),
    queryFn: () =>
      listAllData<LocationRow>("/api/v1/locations", { eventId }),
    select: (r) => r.data,
  })
}

// ── tracks (custom schedule labels attendees filter by) ─────────────────────
// Plain auto-CRUD resources: `tracks` (member+ create/update, admin+ delete) and
// the `agenda_tracks` junction (tag = POST, untag = DELETE; never PATCH — update
// is sysadmin-only). Both are member+ reads (staff only). Neither table emits
// realtime, so these lists refresh only via explicit invalidation after writes.
export type Track = {
  id: string
  eventId: string
  name: string
  description: string | null
  color: string | null
  position: number
}

export type AgendaTrack = {
  id: string
  agendaItemId: string
  trackId: string
  eventId: string
}

export const tracksKey = (eventId: string) => ["resource", "tracks", { eventId }] as const
export const agendaTracksKey = (eventId: string) =>
  ["resource", "agenda-tracks", { eventId }] as const

export function tracksQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: tracksKey(eventId),
    queryFn: () => listAll<Track>("/api/v1/tracks", { eventId }),
    select: (rows) =>
      [...rows].sort((a, b) => a.position - b.position || a.name.localeCompare(b.name)),
  })
}

export function agendaTracksQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: agendaTracksKey(eventId),
    queryFn: () => listAll<AgendaTrack>("/api/v1/agenda-tracks", { eventId }),
  })
}

// Create + tag go through event-scoped actions: generic CRUD can't set the
// system-managed eventId on tracks / agenda_tracks (attendee firewall arm).
// Update / delete / untag stay on the generic routes.
export async function createTrack(input: {
  eventId: string
  name: string
  description?: string | null
  color?: string | null
  position: number
}) {
  const { eventId, ...rest } = input
  const r = await api<{ track: Track }>(`/api/v1/event/${eventId}/tracks/create`, {
    method: "POST",
    body: {
      name: rest.name,
      description: rest.description || undefined,
      color: rest.color || undefined,
      position: rest.position,
    },
  })
  return r.track
}

export function updateTrack(
  id: string,
  patch: Partial<Pick<Track, "name" | "description" | "color" | "position">>,
) {
  return api<Track>(`/api/v1/tracks/${id}`, { method: "PATCH", body: patch })
}

export function deleteTrack(id: string) {
  return api<{ success: true }>(`/api/v1/tracks/${id}`, { method: "DELETE" })
}

export async function tagItem(input: { agendaItemId: string; trackId: string; eventId: string }) {
  const r = await api<{ agendaTrack: AgendaTrack }>(`/api/v1/event/${input.eventId}/tracks/tag`, {
    method: "POST",
    body: { agendaItemId: input.agendaItemId, trackId: input.trackId },
  })
  return r.agendaTrack
}

export function untagItems(ids: string[]) {
  return Promise.all(
    ids.map((id) => api<{ success: true }>(`/api/v1/agenda-tracks/${id}`, { method: "DELETE" })),
  )
}

/** Diff an item's tags to `wanted` track ids: POST the new, DELETE the dropped. */
export async function setItemTracks(
  eventId: string,
  agendaItemId: string,
  current: AgendaTrack[],
  wanted: string[],
) {
  const have = new Set(current.map((r) => r.trackId))
  const want = new Set(wanted)
  await Promise.all([
    ...wanted
      .filter((t) => !have.has(t))
      .map((trackId) => tagItem({ agendaItemId, trackId, eventId })),
    untagItems(current.filter((r) => !want.has(r.trackId)).map((r) => r.id)),
  ])
}

// ── Session record (Activity HQ) bundle ─────────────────────────────────────
export type ActivityParticipant = {
  guestId: string
  rsvpId: string | null
  status: string
  respondedAt: string | null
  checkedInAt: string | null
  checkedOutAt: string | null
  preApproved: number
  waitlistPosition: number | null
  displayName: string
  emailAtInvite: string
  titleAtInvite: string | null
  companyAtInvite: string | null
  linkedUserId: string | null
  implicit?: true
  /** Free-text reason the guest gave when declining a required item/group/
   *  activity ("can't make it"). Null/absent on every other row. */
  note?: string | null
  // Structured event-scoped dietary, projected onto this meal's participant.
  dietary?: ParticipantDietary
}

export type ParticipantDietary = {
  shared: true
  diets: DietEntry[]
  separatePrep: boolean
  allergens: DietaryAllergy[]
  notes: string | null
  anaphylaxis: boolean
  source: string | null
  updatedAt: string | null
} | null

// Per-meal dietary rollup (same backend aggregator as the event report).
export type DietaryRollup = Pick<
  DietaryReport,
  | "total"
  | "sharedCount"
  | "notSharedCount"
  | "plateHeadcount"
  | "tagCounts"
  | "dietCounts"
  | "allergenCounts"
  | "allergenGroupCounts"
  | "customAllergens"
  | "anaphylaxisFlags"
  | "separatePrepCount"
  | "separatePrepFlags"
> & { notShared: { guestId: string; displayName: string }[] }

export type ActivityTodo = {
  id: string
  title: string
  body: string | null
  hasForm: boolean
  responsePolicy: string | null
  createdAt: string
  eligibleCount: number
  completedCount: number
  pending: Array<{ guestId: string; displayName: string }>
}

export type ActivityChannel = {
  id: string
  name: string | null
  topic: string | null
  emoji: string | null
  archivedAt: string | null
}

export type ActivityDetail = {
  success: true
  item: {
    id: string
    eventId: string
    name: string
    description: string | null
    startsAt: string
    endsAt: string
    // Inline address columns were retired in favour of the locations FK; the
    // bundle still returns them when present (older rows) but they're often
    // absent — resolve the location via `locationId` against the locations list.
    venueName?: string | null
    streetAddress?: string | null
    addressLocality?: string | null
    locationId: string | null
    capacity: number | null
    accessMode: string
    rsvpMode: string
    isRequired: number
    isFeatured?: number
    showDirections?: number
    showEndTime?: number
    showOnHome?: number
    sessionRosterVisibility?: RosterVisibility | null
    preApproved: number
    reservationsOpenAt: string | null
    reservationsCloseAt: string | null
    groupId: string | null
    activityId: string | null
    code: string | null
    status: string
    visibility: "public" | "private" | "cohort" | "organizer" | string
    waitlistEnabled: number
    waitlistCapacity: number | null
    waitlistMode?: "queue" | "claim"
  }
  group: { id: string; name: string } | null
  activity: { id: string; name: string; minPerAttendee: number | null; maxPerAttendee: number | null } | null
  rollup: {
    capacity: number | null
    confirmed: number
    pending: number
    declined: number
    checkedIn: number
    total: number
  }
  participants: ActivityParticipant[]
  dietaryRollup?: DietaryRollup
  todos: ActivityTodo[]
  channel: ActivityChannel | null
  state?: {
    state: "open" | "full" | "waitlist" | "closed" | "cancelled" | "not-invited"
    reason: string
  }
}

// Documents attached to a session (or its parent activity). The activity-detail
// bundle does NOT carry documents — the admin SPA fetches the event's full doc
// list separately and filters client-side, so we mirror that exactly (same
// endpoint + cache key as the Documents route, so the two share a cache entry).
export type AttachedDoc = {
  id: string
  slug: string
  title: string
  kind: string
  quickLink: number
  agendaItemId: string | null
  agendaActivityId: string | null
  agendaGroupId: string | null
  attendeeGroupId: string | null
  currentSnapshot: { version: number; publishedAt: string } | null
}

export const eventDocumentsKey = (eventId: string) =>
  ["event-documents", eventId] as const

export function eventDocumentsQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: eventDocumentsKey(eventId),
    queryFn: () =>
      api<{ success: true; documents: AttachedDoc[] }>("/api/v1/event-documents/list", {
        method: "POST",
        body: { eventId },
      }),
    select: (r) => r.documents,
  })
}

export const activityDetailKey = (agendaItemId: string) =>
  ["activity-detail", agendaItemId] as const

export function activityDetailQueryOptions(agendaItemId: string) {
  return queryOptions({
    queryKey: activityDetailKey(agendaItemId),
    queryFn: () =>
      api<ActivityDetail>("/api/v1/agenda-items/get-activity-detail", {
        method: "POST",
        body: { agendaItemId },
      }),
  })
}

// ── create / edit callers (replace the disabled auto-CRUD on agenda_items) ───
// start/end are destination-LOCAL naive strings (YYYY-MM-DDTHH:mm:ss) per the
// app's local-time-only convention. Send only the fields you mean to change on
// update — omitted = unchanged, explicit null clears a nullable field.
export type CreateItemInput = {
  eventId: string
  name: string
  description?: string | null
  startsAt: string
  endsAt: string
  groupId?: string | null
  activityId?: string | null
  locationId?: string | null
  capacity?: number | null
  accessMode?: "open" | "invite-only"
  visibility?: "public" | "private" | "cohort" | "organizer"
  // Set with visibility="cohort" — the attendee group whose members can see +
  // RSVP. Passing it coerces visibility to "cohort"; null clears it.
  audienceGroupId?: string | null
  isRequired?: 0 | 1
  // 1 = shown as a highlighted card on attendees' schedules.
  isFeatured?: 0 | 1
  // 0 = no Directions button for attendees (default 1).
  showDirections?: 0 | 1
  // 1 = attendees' schedule shows "Until <end>" (default 0).
  showEndTime?: 0 | 1
  // 0 = keep off attendees' Home On now / Up next (default 1).
  showOnHome?: 0 | 1
  // Who can see the session roster; null = the event default.
  sessionRosterVisibility?: RosterVisibility | null
  // 0 = RSVPs land in `pending` (host approves each); 1 = auto-confirmed.
  preApproved?: 0 | 1
  rsvpMode?: "required" | "rsvp" | "optional" | "display"
  // Reservation window — naive-local "YYYY-MM-DDTHH:mm:ss" strings (the action
  // enforces that exact shape). null clears the bound; undefined leaves it.
  reservationsOpenAt?: string | null
  reservationsCloseAt?: string | null
  // Short handle ([A-Z0-9-]{1,30}); omit/blank → server auto-generates from name.
  code?: string
  // Waitlist — only meaningful when capacity is set.
  waitlistEnabled?: 0 | 1
  waitlistCapacity?: number | null
  // Only meaningful with waitlistEnabled=1; server defaults to "queue".
  waitlistMode?: "queue" | "claim"
  // Sort order within a group/activity (children pass their row index).
  position?: number
  // Create-time lifecycle status. "confirmed" (default) = published; "draft" =
  // hidden from attendees until published. After create, status moves ONLY via
  // publishItem / unpublishItem / cancel.
  status?: "draft" | "confirmed"
  dryRun?: boolean
}

export type UpdateItemInput = Partial<Omit<CreateItemInput, "eventId">> & {
  id: string
  dryRun?: boolean
}

export function createItem(input: CreateItemInput) {
  return api<{
    success: true
    item?: AgendaItem
    dryRun?: true
    wouldCreate?: Record<string, unknown>
  }>("/api/v1/agenda-items/create", { method: "POST", body: input })
}

export function updateItem(input: UpdateItemInput) {
  return api<{
    success: true
    item?: AgendaItem
    unchanged?: true
    dryRun?: true
    wouldUpdate?: { id: string; patch: Record<string, unknown> }
  }>("/api/v1/agenda-items/update", { method: "POST", body: input })
}

// ── choice-group + activity create callers (parents for the agenda wizard) ───
// A group is a "pick one / pick any" header; an activity is a repeated offering
// with a per-attendee quota. The wizard creates the parent first, then fans out
// child agenda_items (groupId / activityId set) via createItem.
export type CreateGroupInput = {
  eventId: string
  name: string
  description?: string | null
  selectionRule: "one" | "multi"
  allowNone: 0 | 1
  startsAt: string | null
  endsAt: string | null
  position?: number
  dryRun?: boolean
}

export type CreateActivityInput = {
  eventId: string
  name: string
  description?: string | null
  heroImageId?: string | null
  minPerAttendee: number
  maxPerAttendee: number
  position?: number
  dryRun?: boolean
}

export function createGroup(input: CreateGroupInput) {
  return api<{ success?: true; group: { id: string } }>("/api/v1/agenda-groups/create", {
    method: "POST",
    body: input,
  })
}

export type UpdateGroupInput = Partial<Omit<CreateGroupInput, "eventId">> & {
  id: string
  shiftChildren?: boolean
  dryRun?: boolean
}

export function updateGroup(input: UpdateGroupInput) {
  return api<{
    success: true
    group?: AgendaGroup
    unchanged?: true
    dryRun?: true
    wouldUpdate?: { id: string; patch: Record<string, unknown> }
  }>("/api/v1/agenda-groups/update", { method: "POST", body: input })
}

export function createActivity(input: CreateActivityInput) {
  return api<{ success?: true; activity: { id: string } }>(
    "/api/v1/agenda-activities/create",
    { method: "POST", body: input },
  )
}

export type UpdateActivityInput = Partial<Omit<CreateActivityInput, "eventId">> & {
  id: string
  dryRun?: boolean
}

export function updateActivity(input: UpdateActivityInput) {
  return api<{
    success: true
    activity?: AgendaActivity
    unchanged?: true
    dryRun?: true
    wouldUpdate?: { id: string; patch: Record<string, unknown> }
  }>("/api/v1/agenda-activities/update", { method: "POST", body: input })
}

export function duplicateAgendaGroup(id: string) {
  return api<{
    success: true
    group: AgendaGroup
    items: AgendaItem[]
  }>("/api/v1/agenda-groups/duplicate", {
    method: "POST",
    body: { id },
  })
}

export type DeleteAgendaItemPreview = {
  success: true
  dryRun: true
  wouldDelete: {
    id: string
    name: string
    rsvps: number
    todos: number
    todoAssignments: number
    shareLinks: number
    reminders: number
  }
}

export function deleteAgendaItemDryRun(id: string) {
  return api<DeleteAgendaItemPreview>("/api/v1/agenda-items/delete", {
    method: "POST",
    body: { id, dryRun: true },
  })
}

export function deleteAgendaItem(id: string) {
  return api<{
    success: true
    itemId: string
    deleted: Omit<DeleteAgendaItemPreview["wouldDelete"], "id" | "name">
  }>(
    "/api/v1/agenda-items/delete",
    { method: "POST", body: { id } },
  )
}

export type DeleteAgendaGroupPreview = {
  success: true
  dryRun: true
  wouldDelete: {
    id: string
    name: string
    items: number
    rsvps: number
    todos: number
    todoAssignments: number
    shareLinks: number
    reminders: number
  }
}

export function deleteAgendaGroupDryRun(id: string) {
  return api<DeleteAgendaGroupPreview>("/api/v1/agenda-groups/delete", {
    method: "POST",
    body: { id, dryRun: true },
  })
}

export function deleteAgendaGroup(id: string) {
  return api<{
    success: true
    groupId: string
    deleted: Omit<DeleteAgendaGroupPreview["wouldDelete"], "id" | "name">
  }>(
    "/api/v1/agenda-groups/delete",
    { method: "POST", body: { id } },
  )
}

export type DeleteAgendaActivityPreview = {
  success: true
  dryRun: true
  wouldDelete: {
    id: string
    name: string
    items: number
    rsvps: number
    todos: number
    todoAssignments: number
    shareLinks: number
    reminders: number
  }
}

export function deleteAgendaActivityDryRun(id: string) {
  return api<DeleteAgendaActivityPreview>("/api/v1/agenda-activities/delete", {
    method: "POST",
    body: { id, dryRun: true },
  })
}

export function deleteAgendaActivity(id: string) {
  return api<{
    success: true
    activityId: string
    deleted: Omit<DeleteAgendaActivityPreview["wouldDelete"], "id" | "name">
  }>(
    "/api/v1/agenda-activities/delete",
    { method: "POST", body: { id } },
  )
}

// ── lifecycle mutation callers (status moves ONLY via these POST actions) ────
export type PublishPreview = {
  success: true
  dryRun: true
  wouldPublish: { id: string; name: string; previousStatus: string }
}

export function publishItemDryRun(id: string) {
  return api<PublishPreview>("/api/v1/agenda-items/publish", {
    method: "POST",
    body: { id, dryRun: true },
  })
}

export function publishItem(id: string, dryRun = false) {
  return api<{ success: true; status: string; wouldPublish?: { previousStatus: string } }>(
    "/api/v1/agenda-items/publish",
    { method: "POST", body: { id, dryRun } },
  )
}

export function unpublishItem(id: string, dryRun = false) {
  return api<{
    success: true
    status?: string
    wouldUnpublish?: { previousStatus: string }
  }>("/api/v1/agenda-items/unpublish", { method: "POST", body: { id, dryRun } })
}

export type CancelPreview = {
  success: true
  dryRun: true
  wouldCancel: { id: string; name: string; rsvpsAffected: number; channelId: string | null }
}

export function cancelItemDryRun(id: string) {
  return api<CancelPreview>("/api/v1/agenda-items/cancel", {
    method: "POST",
    body: { id, dryRun: true },
  })
}

export function cancelItem(id: string, reason?: string) {
  return api<{ success: true; rsvpsAffected: number; channelId: string | null }>(
    "/api/v1/agenda-items/cancel",
    { method: "POST", body: { id, reason: reason?.trim() || undefined } },
  )
}

// ── participant mutation callers ────────────────────────────────────────────
export function checkInGuest(agendaItemId: string, guestId: string) {
  return api<{
    success: true
    cascadedOutOf: Array<{ itemName: string }>
    cascadedCount: number
  }>("/api/v1/agenda-items/check-in", {
    method: "POST",
    body: { agendaItemId, guestId },
  })
}

export function checkOutGuest(agendaItemId: string, guestId: string) {
  return api<{ success: true }>("/api/v1/agenda-items/check-out", {
    method: "POST",
    body: { agendaItemId, guestId },
  })
}

// Undo a check-in entirely (back to confirmed / not checked-in).
export function cancelCheckInGuest(agendaItemId: string, guestId: string) {
  return api<{ success: true }>("/api/v1/agenda-items/cancel-check-in", {
    method: "POST",
    body: { agendaItemId, guestId },
  })
}

// Undo a check-out (back to checked-in).
export function cancelCheckOutGuest(agendaItemId: string, guestId: string) {
  return api<{ success: true }>("/api/v1/agenda-items/cancel-check-out", {
    method: "POST",
    body: { agendaItemId, guestId },
  })
}

// `note` = the staff-entered "can't make it" reason. Only meaningful on a
// decline; blank/whitespace is omitted so we never write an empty note.
export function setRsvp(
  agendaItemId: string,
  guestId: string,
  response: "confirmed" | "declined",
  note?: string,
) {
  return api<{ success: true }>("/api/v1/admin/agenda-rsvp", {
    method: "POST",
    body: { agendaItemId, guestId, response, ...noteBody(note) },
  })
}

function noteBody(note?: string): { note?: string } {
  const t = note?.trim()
  return t ? { note: t.slice(0, 500) } : {}
}

// "None of these" / clear a choice group: declines every item in the group for
// the guest (the unselect path for pick-one / pick-any groups).
export function declineGroup(agendaGroupId: string, guestId: string, note?: string) {
  return api<{ success: true; declined?: number }>("/api/v1/admin/agenda-decline-group", {
    method: "POST",
    body: { agendaGroupId, guestId, ...noteBody(note) },
  })
}

export function bulkInvite(agendaItemId: string, guestIds: string[]) {
  return api<{ success: true; added: number; alreadyInvited: number; skipped: number }>(
    "/api/v1/agenda-items/bulk-invite",
    { method: "POST", body: { agendaItemId, guestIds } },
  )
}

// ── per-meal catering operations ────────────────────────────────────────────
export type ActivityCatering = {
  success: true
  agendaItemName: string
  restaurant: { id: string; name: string } | null
  cateringCutoffAt: string | null
  lastExportedAt: string | null
  changedSinceExport: { guestId: string; displayName: string; updatedAt: string | null }[]
  total: number
  sharedCount: number
  notSharedCount: number
  anaphylaxisFlags: { guestId: string; displayName: string; kinds: string[] }[]
}

export function activityCateringQueryOptions(agendaItemId: string) {
  return queryOptions({
    queryKey: ["activity-catering", agendaItemId] as const,
    queryFn: () =>
      api<ActivityCatering>("/api/v1/agenda-items/dietary-report", {
        method: "POST",
        body: { agendaItemId },
      }),
  })
}

export function setActivityCatering(
  agendaItemId: string,
  body: { cateringCutoffAt?: string | null; catererName?: string | null; catererEmail?: string | null },
) {
  return api<{ success: true }>("/api/v1/agenda-items/set-catering", {
    method: "POST",
    body: { agendaItemId, ...body },
  })
}

export function exportActivityCateringSheet(agendaItemId: string) {
  return api<{ success: true; csv?: string; agendaItemName: string }>(
    "/api/v1/agenda-items/catering-sheet",
    { method: "POST", body: { agendaItemId, format: "csv" } },
  )
}

// ── public share links (participant list / kitchen sheet) ───────────────────
// Backed by the FGA share-link actions; rendered no-login at share.attend.vip.
export type ShareLinkKind = "participants" | "kitchen"

export type ActivityShareLink = {
  kind: ShareLinkKind
  url: string
  token: string
  live: boolean
  expiresAt: string | null
  revokedAt: string | null
  createdAt: string | null
}

export function listActivityShareLinksReq(agendaItemId: string) {
  return api<{ success: true; links: ActivityShareLink[] }>(
    "/api/v1/agenda-items/list-share-links",
    { method: "POST", body: { agendaItemId } },
  )
}

export function activityShareLinksQueryOptions(agendaItemId: string) {
  return queryOptions({
    queryKey: ["activity-share-links", agendaItemId] as const,
    queryFn: () => listActivityShareLinksReq(agendaItemId),
  })
}

// Every share link on the event in one round-trip — activity participant /
// kitchen links AND form-response links, labelled server-side. Backs the
// console's "Shared access" tab, which used to fan out one request per agenda
// item + per form.
export type EventShareLink = {
  kind: ShareLinkKind | "form_responses"
  url: string
  token: string
  live: boolean
  agendaItemId: string | null
  todoId: string | null
  label: string | null
  expiresAt: string | null
  revokedAt: string | null
  createdAt: string | null
}

export function eventShareLinksQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: ["event-share-links", eventId] as const,
    queryFn: () =>
      api<{ success: true; links: EventShareLink[] }>(
        "/api/v1/agenda-items/list-event-share-links",
        { method: "POST", body: { eventId } },
      ),
  })
}

export function createActivityShareLink(agendaItemId: string, kind: ShareLinkKind, ttlDays?: number) {
  return api<{ success: true; kind: ShareLinkKind; token: string; url: string; expiresAt: string | null }>(
    "/api/v1/agenda-items/create-share-link",
    { method: "POST", body: { agendaItemId, kind, ttlDays } },
  )
}

export function revokeActivityShareLink(token: string) {
  return api<{ success: true; revokedCount: number; revokedTokens: string[] }>(
    "/api/v1/agenda-items/revoke-share-link",
    { method: "POST", body: { token } },
  )
}

// ── waitlist mutation callers ───────────────────────────────────────────────
export function promoteWaitlist(agendaItemId: string, count: number) {
  return api<{ success: true; promoted: unknown[] }>(
    "/api/v1/agenda-items/waitlist/promote",
    { method: "POST", body: { agendaItemId, count } },
  )
}

export function removeFromWaitlist(agendaItemId: string, guestId: string) {
  return api<{ success: true }>("/api/v1/agenda-items/waitlist/remove", {
    method: "POST",
    body: { agendaItemId, guestIds: [guestId] },
  })
}

export function reorderWaitlist(agendaItemId: string, guestId: string, toPosition: number) {
  return api<{ success: true }>("/api/v1/agenda-items/waitlist/reorder", {
    method: "POST",
    body: { agendaItemId, guestId, toPosition },
  })
}

// ── shared display helpers (naive local-time strings per project convention) ─
export type ChipTone =
  | "required"
  | "rsvp"
  | "invite"
  | "open"
  | "display"
  | "draft"
  | "cancelled"
  | "group"

export function attendanceChip(item: {
  rsvpMode: string
  isRequired: number
  accessMode: string
}): { tone: ChipTone; label: string } {
  if (item.rsvpMode === "required" || item.isRequired === 1)
    return { tone: "required", label: "Required" }
  if (item.accessMode === "invite-only") return { tone: "invite", label: "Invite-only" }
  if (item.rsvpMode === "rsvp") return { tone: "rsvp", label: "RSVP" }
  if (item.rsvpMode === "display") return { tone: "display", label: "Display" }
  return { tone: "open", label: "Open" }
}

export function dayKey(naive: string): string {
  return naive.slice(0, 10)
}

export function minutesOfDay(naive: string): number {
  const t = naive.slice(11, 16)
  const [h, m] = t.split(":").map(Number)
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0)
}

export function parseDay(yyyymmdd: string): Date | null {
  const parts = yyyymmdd.split("-")
  if (parts.length !== 3) return null
  return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]))
}

export function formatDayLong(yyyymmdd: string): string {
  const d = parseDay(yyyymmdd)
  if (!d) return yyyymmdd
  return d.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: d.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined,
  })
}

export function formatWeekday(yyyymmdd: string): string {
  const d = parseDay(yyyymmdd)
  if (!d) return yyyymmdd
  return d.toLocaleDateString(undefined, { weekday: "short" })
}

export function formatDayMonth(yyyymmdd: string): string {
  const d = parseDay(yyyymmdd)
  if (!d) return yyyymmdd
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" })
}

export function formatTime(naive: string): string {
  const t = naive.slice(11, 16)
  if (!t) return ""
  const [h, m] = t.split(":").map(Number)
  if (!Number.isFinite(h)) return ""
  const ampm = h >= 12 ? "PM" : "AM"
  const h12 = ((h + 11) % 12) + 1
  return `${h12}:${(m || 0).toString().padStart(2, "0")} ${ampm}`
}

export function formatRange(startsAt: string, endsAt: string): string {
  const startDay = startsAt.slice(0, 10)
  const endDay = endsAt.slice(0, 10)
  const startTime = formatTime(startsAt)
  const endTime = formatTime(endsAt)
  if (startDay === endDay) {
    return `${formatDayMonth(startDay)} · ${startTime}–${endTime}`
  }
  return `${formatDayMonth(startDay)} ${startTime} → ${formatDayMonth(endDay)} ${endTime}`
}

export function locationPath(
  locationId: string | null,
  locationsById: Map<string, LocationRow>,
  // Order of the breadcrumb. Default is root→leaf ("Venue / Room") — the
  // conventional general→specific order used in pickers and the grid. Pass
  // { leafFirst: true } to lead with the specific child ("Room / Venue").
  opts?: { leafFirst?: boolean },
): string | null {
  if (locationId == null) return null
  const names: string[] = []
  const seen = new Set<string>()
  let current = locationsById.get(locationId)
  while (current && !seen.has(current.id)) {
    seen.add(current.id)
    names.unshift(current.name)
    current = current.parentLocationId != null
      ? locationsById.get(current.parentLocationId)
      : undefined
  }
  if (names.length === 0) return null
  return (opts?.leafFirst ? [...names].reverse() : names).join(" / ")
}

// ── grouping helpers ────────────────────────────────────────────────────────
export type EmptyAgendaGroup = AgendaGroup & { startsAt: string; endsAt?: string | null }
export type DayGroup = { date: string; items: AgendaItem[]; emptyGroups: EmptyAgendaGroup[] }

export function groupByDay(items: AgendaItem[], emptyGroups: EmptyAgendaGroup[] = []): DayGroup[] {
  const map = new Map<string, DayGroup>()
  for (const i of items) {
    const day = dayKey(i.startsAt)
    if (!map.has(day)) map.set(day, { date: day, items: [], emptyGroups: [] })
    map.get(day)!.items.push(i)
  }
  for (const g of emptyGroups) {
    const day = dayKey(g.startsAt)
    if (!map.has(day)) map.set(day, { date: day, items: [], emptyGroups: [] })
    map.get(day)!.emptyGroups.push(g)
  }
  return Array.from(map.values())
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((day) => ({
      ...day,
      items: day.items.sort((a, b) => a.startsAt.localeCompare(b.startsAt)),
      emptyGroups: day.emptyGroups.sort((a, b) => a.startsAt.localeCompare(b.startsAt)),
    }))
}

export function clusterByStartTime(
  items: AgendaItem[],
): { startsAt: string; endsAt: string | null; items: AgendaItem[] }[] {
  const slots: { startsAt: string; endsAt: string | null; items: AgendaItem[] }[] = []
  for (const it of items) {
    const last = slots[slots.length - 1]
    if (last && last.startsAt === it.startsAt) {
      last.items.push(it)
      if (it.endsAt > (last.endsAt ?? "")) last.endsAt = it.endsAt
    } else {
      slots.push({ startsAt: it.startsAt, endsAt: it.endsAt, items: [it] })
    }
  }
  return slots
}

export function commonSlotGroup(
  items: AgendaItem[],
  groupsById: Map<string, AgendaGroup>,
): AgendaGroup | null {
  const groupId = items[0]?.groupId
  if (groupId == null) return null
  if (!items.every((item) => item.groupId === groupId)) return null
  return groupsById.get(groupId) ?? null
}

export function commonSlotActivity(
  items: AgendaItem[],
  activitiesById: Map<string, AgendaActivity>,
): AgendaActivity | null {
  const activityId = items[0]?.activityId
  if (activityId == null) return null
  if (!items.every((item) => item.activityId === activityId)) return null
  return activitiesById.get(activityId) ?? null
}

export function choiceLabel(group: AgendaGroup, count: number): string {
  if (group.selectionRule === "multi") return `Pick any of ${count}`
  return `Pick one of ${count}`
}
